interface TransactionDisplayInput {
  description?: string;
  type?: string;
  transactionId?: string;
  metadata?: Record<string, any>;
}

/**
 * Instant Payment / Request to Pay value row. The fee row uses RPP-FEE- / RTP-FEE-.
 * A customer reference may contain the word "fee" and must stay as typed.
 */
function isPayshapPrincipalTransaction(transaction: TransactionDisplayInput): boolean {
  const id = String(transaction.transactionId || '');
  if (!id) return false;
  if (/^RPP-FEE-/i.test(id) || /^RPP-REV-/i.test(id) || /^RTP-FEE-/i.test(id)) return false;
  return /^RPP-/i.test(id) || /^RTP-/i.test(id);
}

function declinedInstantPaymentParts(transactionId?: string): { kind: 'feeRefund' | 'principalRefund'; mti: string } | null {
  const id = String(transactionId || '');
  if (/^RPP-REV-FEE-/i.test(id)) return { kind: 'feeRefund', mti: id.slice('RPP-REV-FEE-'.length) };
  if (/^RPP-REV-/i.test(id)) return { kind: 'principalRefund', mti: id.slice('RPP-REV-'.length) };
  return null;
}

export function declinedInstantPaymentLabel(transaction: TransactionDisplayInput): string {
  const metadata = transaction.metadata || {};
  const bank = String(metadata.bankName || '').replace(/\s+/g, ' ').trim();
  const last4 = String(metadata.accountNumberLast4 || '').replace(/\D/g, '').slice(-4);
  if (bank && last4) return `Instant Payment declined — ${bank} ••${last4} — refunded`;
  if (bank) return `Instant Payment declined — ${bank} — refunded`;
  if (last4) return `Instant Payment declined — ••${last4} — refunded`;
  return 'Instant Payment declined — refunded';
}

/**
 * The ledger keeps the principal refund and the fee refund. The customer list
 * shows one net-zero line.
 */
export function collapseDeclinedInstantPayments<T extends TransactionDisplayInput & { amount?: number }>(
  transactions: T[]
): T[] {
  return transactions.flatMap((transaction) => {
    const parsed = declinedInstantPaymentParts(transaction.transactionId);
    if (!parsed) return [transaction];
    if (parsed.kind === 'feeRefund') return [];
    return [{
      ...transaction,
      amount: 0,
      description: declinedInstantPaymentLabel(transaction),
    }];
  });
}

export function cleanTransactionDescription(transaction: TransactionDisplayInput): string {
  if (declinedInstantPaymentParts(transaction.transactionId)?.kind === 'principalRefund') {
    return declinedInstantPaymentLabel(transaction);
  }

  const description = (transaction.description || '').replace(/\s+/g, ' ').trim();
  if (!description) return 'Transaction';

  const metadata = transaction.metadata || {};
  const lowerDescription = description.toLowerCase();
  const payshapPrincipal = isPayshapPrincipalTransaction(transaction);
  const isFeeTransaction =
    transaction.type === 'fee' ||
    metadata.isTopUpFee === true ||
    metadata.isFlashCashoutFee === true ||
    metadata.isCashoutFee === true ||
    metadata.isEasyPayVoucherFee === true ||
    (!payshapPrincipal && (
      String(metadata.operationType || '').toLowerCase().includes('fee') ||
      String(metadata.settlementType || '').toLowerCase().includes('fee') ||
      /\bfee\b/i.test(description)
    ));

  if (isFeeTransaction) return 'Transaction fee';

  if (String(metadata.vasType || '').toLowerCase() === 'global_services') {
    const productName = String(metadata.productName || '').trim();
    if (transaction.type === 'refund' || metadata.reversed) {
      return productName ? `Global Services refund — ${productName}` : 'Global Services refund';
    }
    return productName ? `Global Services — ${productName}` : 'Global Services';
  }

  const providerCode = String(metadata.providerCode || '');
  const serviceFamily = String(metadata.serviceFamily || '').toLowerCase();
  const isPayshap =
    ['66', '127'].includes(providerCode) ||
    serviceFamily === 'payshap' ||
    lowerDescription.includes('payshap');
  const providerLabel =
    ['112', '67'].includes(providerCode) ? 'ABSA CashSend PIN' :
    ['10', '4'].includes(providerCode) ? 'Nedbank CashSend PIN' :
    lowerDescription.includes('absa') ? 'ABSA CashSend PIN' :
    lowerDescription.includes('nedbank') ? 'Nedbank CashSend PIN' :
    'Withdraw Cash';

  if (metadata.combinedOttPayoutRefund || /^ott payout reversal:/i.test(description)) {
    if (isPayshap) {
      const beneficiaryName = String(metadata.beneficiaryName || metadata.accountName || '').replace(/\s+/g, ' ').trim();
      return beneficiaryName ? `PayShap refund - ${beneficiaryName}` : 'PayShap refund';
    }
    return `Withdraw Cash refund - ${providerLabel}`;
  }

  if (metadata.combinedOttPayout || metadata.ottPayoutId) {
    if (isPayshap) {
      const beneficiaryName = String(metadata.beneficiaryName || metadata.accountName || '').replace(/\s+/g, ' ').trim();
      return beneficiaryName ? `PayShap to ${beneficiaryName}` : 'PayShap to bank';
    }
    return `Withdraw Cash - ${providerLabel}`;
  }

  const isBankOriginDeposit =
    transaction.type === 'deposit' ||
    transaction.type === 'money_in' ||
    metadata.source === 'SBSA_DEPOSIT_NOTIFICATION' ||
    metadata.sbsaTransactionId ||
    metadata.inboundCreditSource === 'h2h_statement_trf' ||
    metadata.inboundCreditSource === 'payshap_inbound';

  const hasRawBankNarrative =
    /\/PREF\//i.test(description) ||
    /ZA\d{6,}PAYSHAP/i.test(description) ||
    /PAYSHAP PAYMENT FROM/i.test(description);

  if (isBankOriginDeposit && hasRawBankNarrative) {
    return 'Deposit';
  }

  return description;
}
